import React, { useEffect, useState } from 'react';
import { Heading, Image, Link, RichText, Pagination, MemberIcon } from '@/components';
import Cookies from 'universal-cookie';
import { bgColorClassname } from '@/utils';
import { Button } from '@/components/ui/button';
import { startOfMonth, addMonths, isWithinInterval } from 'date-fns';
import { formatTimeRange } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { getEvents, getMonthNames, getMonthColor } from '@/lib/events';
import { Event } from '@/types/EventInterface';
import { ArrowRight } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';

interface EventListingProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  title?: string;
}

// Each occurrence has startTime/endTime + optional exception
interface EventOccurrence {
  event: Event;
  date: Date;
  startTime: Date;
  endTime: Date;
  exception?: {
    cancelEvent?: boolean;
    modifiedStartTime?: Date;
    modifiedEndTime?: Date;
  };
}

/**
 * EventListing Component
 * Displays a paginated list of events with filtering capabilities
 * Supports filtering by month and member-only events
 */
const EventListing: React.FC<EventListingProps> = ({ backgroundColor, title }) => {
  // State management for loading, events data and filters
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState<string[]>([]);
  const [eventOccurrences, setEventOccurrences] = useState<EventOccurrence[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventOccurrence[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [showMemberOnly, setShowMemberOnly] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'member' | 'month'>('all');
  const itemsPerPage = 10;

  const cookies = new Cookies();
  const language = cookies.get('preferredLanguage');
  const EST_TIMEZONE = 'America/New_York';
  const SCROLL_OFFSET = 20;

  // Helper function to update URL query string
  const updateURLFilter = (filterValue: string | null) => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (filterValue && filterValue !== 'all') {
        url.searchParams.set('filter', filterValue);
      } else {
        url.searchParams.delete('filter');
      }
      window.history.pushState({}, '', url.toString());
    }
  };

  // Fetch events on component mount and language change
  useEffect(() => {
    const fetchEvents = async () => {
      const startDate = startOfMonth(new Date());
      setMonths(getMonthNames(startDate));

      // Now returns an array of { event, date, startTime, endTime, exception? }
      const occurrences = await getEvents(language);
      setEventOccurrences(occurrences);
      setLoading(false);
    };

    fetchEvents();
  }, [language]);

  // Check for filter query parameter on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const filterParam = urlParams.get('filter');

      if (filterParam) {
        if (filterParam === 'member') {
          setActiveFilter('member');
          setShowMemberOnly(true);
          setSelectedMonth(null);
        } else if (months.includes(filterParam)) {
          setActiveFilter('month');
          setSelectedMonth(filterParam);
          setShowMemberOnly(false);
        } else if (filterParam === 'all') {
          setActiveFilter('all');
          setSelectedMonth(null);
          setShowMemberOnly(false);
        }
      }
    }
  }, [months]);

  // Filter events based on selected month and member-only status
  useEffect(() => {
    let updatedEvents = eventOccurrences;

    // Apply month filter if selected
    if (selectedMonth) {
      const monthIndex = months.indexOf(selectedMonth);
      const startOfSelectedMonth = addMonths(startOfMonth(new Date()), monthIndex);
      const endOfSelectedMonth = addMonths(startOfSelectedMonth, 1);

      updatedEvents = updatedEvents.filter(({ date }) =>
        isWithinInterval(date, { start: startOfSelectedMonth, end: endOfSelectedMonth }),
      );
    }

    // Apply member-only filter if selected
    if (showMemberOnly) {
      updatedEvents = updatedEvents.filter(({ event }) => event.isMemberOnly);
    }

    setFilteredEvents(updatedEvents);
    setCurrentPage(1);
  }, [eventOccurrences, selectedMonth, showMemberOnly, months]);

  /**
   * Handles pagination changes in the event listing.
   * Ensures that after a new page is loaded, the view scrolls smoothly
   * to the top of the event list, enhancing UX especially when
   * navigating from the bottom of the list.
   */
  const handlePageChange = (page: number) => {
    // Ensure page number stays within valid range
    if (page < 1) page = 1;
    if (page > Math.ceil(filteredEvents.length / itemsPerPage)) {
      page = Math.ceil(filteredEvents.length / itemsPerPage);
    }
    // Update the current page number before triggering scroll
    setCurrentPage(page);

    // Delay scroll until next paint to ensure DOM updates
    requestAnimationFrame(() => {
      // Scroll to top of event list container, adjusted by SCROLL_OFFSET
      const listingElement = document.getElementById('event-listing-section');
      if (listingElement) {
        window.scrollTo({
          top: listingElement.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET,
          behavior: 'smooth',
        });
      }
    });
  };

  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='mx-auto max-w-screen-xl px-5 py-8 md:gap-x-6 md:p-[50px] lg:gap-x-12 xl:gap-x-24'>
        <div
          id='event-listing-section'
          className='w-full'
        >
          {title && (
            <Heading
              title={title}
              className='mb-3 md:mb-8'
            />
          )}

          {/* Filter Buttons */}
          <div className='mb-8 flex flex-wrap-reverse items-center justify-between gap-4 md:flex-nowrap md:gap-2'>
            <div className='flex w-full flex-1 flex-wrap gap-2 md:w-auto'>
              <Button
                variant={activeFilter === 'all' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  setActiveFilter('all');
                  setSelectedMonth(null);
                  setShowMemberOnly(false);
                  setCurrentPage(1);
                  updateURLFilter('all');
                }}
              >
                all events
              </Button>
              {months.map((month, index) => (
                <Button
                  key={`month-${index}`}
                  variant={selectedMonth === month ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => {
                    setSelectedMonth(month);
                    setActiveFilter('month');
                    setShowMemberOnly(false);
                    setCurrentPage(1);
                    updateURLFilter(month);
                  }}
                >
                  {month}
                </Button>
              ))}
              {!loading && (
                <Button
                  variant={activeFilter === 'member' ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => {
                    setShowMemberOnly(!showMemberOnly);
                    setSelectedMonth(null);
                    setActiveFilter('member');
                    setCurrentPage(1);
                    updateURLFilter('member');
                  }}
                >
                  member only
                </Button>
              )}
            </div>
            <div className='flex w-full items-center justify-end md:w-auto md:justify-center'>
              <span>
                <MemberIcon
                  width={19}
                  height={19}
                />
              </span>
              <span className='ml-1 text-base font-normal leading-5'>
                indicates member only event
              </span>
            </div>
          </div>

          {/* Loading Skeleton */}
          {loading ? (
            <div className='flex flex-col gap-y-5'>
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className='flex flex-wrap items-center gap-2 md:flex-nowrap'
                >
                  <div className='flex w-full items-center gap-2 self-stretch md:w-auto'>
                    <Skeleton className='h-40 w-1/3 rounded-none rounded-tl-xl md:h-36 md:w-28 md:rounded-l-xl md:rounded-r-none' />
                    <Skeleton className='h-40 w-2/3 rounded-none rounded-tr-xl md:w-40 md:rounded-xl' />
                  </div>
                  <Skeleton className='h-36 w-full rounded-none rounded-b-xl md:w-auto md:flex-1 md:rounded-l-none md:rounded-r-xl' />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className='flex flex-col gap-y-5'>
                {filteredEvents
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((occurrence, index) => {
                    const { event, date, startTime, endTime, exception } = occurrence;

                    // If the exception indicates the event is canceled, skip rendering
                    if (exception?.cancelEvent) {
                      return null;
                    }

                    // If there's a modifiedStartTime/EndTime in the exception, override
                    const displayStart = exception?.modifiedStartTime ?? startTime;
                    const displayEnd = exception?.modifiedEndTime ?? endTime;

                    return (
                      <div
                        key={index}
                        className='relative flex flex-wrap items-center md:flex-nowrap'
                      >
                        {/* Left Side: Date + Image */}
                        <div className='flex items-center self-stretch'>
                          <div
                            className={`relative flex h-full w-28 flex-col items-center justify-center rounded-none rounded-tl-xl lowercase md:h-36 md:w-24 md:rounded-l-xl md:rounded-r-none ${getMonthColor(
                              date.toString(),
                            )}`}
                          >
                            {(showMemberOnly || event.isMemberOnly) && (
                              <div className='absolute -left-4 -top-2'>
                                <MemberIcon
                                  width={34}
                                  height={34}
                                />
                              </div>
                            )}
                            <div className='text-sm font-normal leading-5'>
                              {formatInTimeZone(date, EST_TIMEZONE, 'MMMM')}
                            </div>
                            <div className='text-5xl font-bold leading-[48px]'>
                              {formatInTimeZone(date, EST_TIMEZONE, 'd')}
                            </div>
                            <div className='text-sm font-normal leading-5'>
                              {formatInTimeZone(date, EST_TIMEZONE, 'eeee')}
                            </div>
                          </div>
                          {event.featuredImage && (
                            <div className='z-10 h-full max-h-72 w-full overflow-hidden rounded-none rounded-tr-xl md:h-40 md:max-h-none md:w-40 md:rounded-xl'>
                              <Image
                                className='h-full w-full object-cover md:h-40 md:w-40'
                                image={event.featuredImage.image}
                                alt={event.featuredImage?.alt || event.title}
                                width={160}
                              />
                            </div>
                          )}
                        </div>

                        {/* Right Side: Title, Blurb, Time/Location, CTA */}
                        <div className='flex w-full items-center justify-between rounded-xl bg-white p-4 md:ml-[-24px] md:w-auto md:flex-1 md:p-5 md:pl-12'>
                          <div className='mr-5 flex w-full flex-col'>
                            <h3 className='mb-2 text-2xl font-semibold leading-5'>{event.title}</h3>
                            <RichText
                              value={event.blurb}
                              className='text-[16px] font-light leading-tight'
                            />
                            <hr className='border-blackTrans15 mb-2 mt-4' />
                            <div className='flex flex-wrap gap-x-5 gap-y-2'>
                              {/* Time */}
                              <div className='flex items-center'>
                                <img
                                  src='/images/time-icon.svg'
                                  className='mr-1'
                                  alt='Time Icon'
                                />
                                <span className='text-sm font-normal lowercase leading-5'>
                                  {formatTimeRange(displayStart, displayEnd)} EST
                                </span>
                              </div>
                              {/* Location */}
                              {event.location && (
                                <div className='flex items-center'>
                                  <img
                                    src='/images/location-icon.svg'
                                    className='mr-1'
                                    alt='Location Icon'
                                  />
                                  <span className='text-sm font-normal leading-5'>
                                    {event.location}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <Link
                            internalLink={{
                              slug: { current: event.slug.current },
                              language: event.language,
                              _type: 'event',
                            }}
                            variant='default'
                            size='sm'
                            ariaLabel={event.title}
                            className='h-[40px] w-[40px]'
                          >
                            <ArrowRight />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {filteredEvents.length > itemsPerPage && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={filteredEvents.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                />
              )}

              {filteredEvents.length === 0 && (
                <div className='text-center text-xl font-bold text-gray-500'>
                  no events were found.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventListing;
